import React, { useState } from "react";
import QrUploadScanner from "../../../components/QrUploadScanner/QrUploadScanner";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";

const TrackQrHistory = () => {
  // States: 'idle', 'extracting', 'fetching', 'success', 'error'
  const [status, setStatus] = useState("idle");
  const [qrValue, setQrValue] = useState(null);
  const [details, setDetails] = useState(null);

  const handleScanStart = () => {
    setStatus("extracting");
    setQrValue(null);
    setDetails(null);
  };

  const handleScanError = (err) => {
    setStatus("error");
  };

  const handleScanSuccess = (value) => {
    setQrValue(value);
    setStatus("fetching");

    // Fake API call delay
    setTimeout(() => {
      setStatus("success");
      setDetails({
        productId: "PRD-909812",
        productName: "Premium Wireless Headphones",
        batchNo: "BCH-2023-X1",
        manufacturedDate: "2023-10-15",
        status: "Active",
        scannedLocation: "New Delhi, India",
        pointsAwarded: 50
      });
      toast.success("Details fetched successfully!");
    }, 2000);
  };

  return (
    <div className="flex w-full bg-gray-200 h-full justify-center">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
          Track QR History
          <LinksComponent />
        </div>
        <div className="p-3">
          <div className="bg-white rounded-lg shadow-sm p-6 w-full mx-auto min-h-[500px]">
            <p className="text-gray-600 mb-8">
              Upload an image of a QR code to extract its value and view tracking history.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-full">
              {/* Left Column: Uploader */}
              <div className="flex flex-col border-r-0 md:border-r border-gray-200 md:pr-8">
                <h2 className="text-lg font-medium text-gray-800 mb-4">1. Upload Image</h2>
                <div className="flex-1 flex flex-col justify-center h-full min-h-[300px]">
                  <QrUploadScanner onScanStart={handleScanStart} onScanSuccess={handleScanSuccess} onScanError={handleScanError} />
                </div>
              </div>

              {/* Right Column: Status & Details */}
              <div className="flex flex-col">
                <h2 className="text-lg font-medium text-gray-800 mb-4">2. Tracking Information</h2>

                <div className="flex-1 bg-gray-50 rounded-lg p-6 border border-gray-100 flex flex-col min-h-[300px]">

                  {status === "idle" && (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                      Waiting for image upload...
                    </div>
                  )}

                  {status === "extracting" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-blue-600">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                      <p className="font-medium animate-pulse">Extracting QR Code from image...</p>
                    </div>
                  )}

                  {status === "fetching" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-purple-600">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-4"></div>
                      <p className="font-medium">QR Code: <span className="text-gray-800">{qrValue}</span></p>
                      <p className="font-medium animate-pulse mt-2">Fetching tracking details from API...</p>
                    </div>
                  )}

                  {status === "success" && details && (
                    <div className="flex-1 flex flex-col">
                      <div className="bg-green-50 text-green-700 p-3 rounded mb-6 border border-green-200 text-sm break-all">
                        <strong>Extracted QR Code:</strong> {qrValue}
                      </div>

                      <h3 className="font-semibold text-gray-700 mb-4 border-b pb-2">Product Details</h3>
                      <div className="grid grid-cols-2 gap-y-5 gap-x-6 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Product Name</p>
                          <p className="font-medium text-gray-800">{details.productName}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Product ID</p>
                          <p className="font-medium text-gray-800">{details.productId}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Batch Number</p>
                          <p className="font-medium text-gray-800">{details.batchNo}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Mfg Date</p>
                          <p className="font-medium text-gray-800">{details.manufacturedDate}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Current Status</p>
                          <p className="font-medium text-gray-800 mt-1">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">{details.status}</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Points Awarded</p>
                          <p className="font-medium text-green-600">{details.pointsAwarded} pts</p>
                        </div>
                        <div className="col-span-2 mt-2">
                          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Last Scanned Location</p>
                          <p className="font-medium text-gray-800">{details.scannedLocation}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-red-500">
                      <div className="mb-4 text-4xl">❌</div>
                      <p className="font-medium text-gray-700">Failed to extract QR Code</p>
                      <p className="text-sm text-gray-500 mt-2">Please upload a valid QR code image and try again.</p>
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackQrHistory;
